<template>
  <section id="reviews" :class="['reviews', { 'reviews--no-reviews': hasNoReviews }]">
    <div class="reviews__inner">
      <h3 class="reviews__headline">
        {{ $t('headline', { product: product.name }) }}
      </h3>
      <transition name="fade">
        <div v-show="hasFetchedFirstReviews">
          <div class="reviews__average">
            <FiveStarsRating :rating="reviewTotal > 0 ? reviewRatingAverage : 0" />
          </div>
          <div
            :class="[
              'reviews__average-hint',
              { 'reviews__average-hint--no-reviews': hasNoReviews },
            ]"
          >
            {{ $tc('rating_average_hint', reviewTotal, { count: reviewTotal }) }}
          </div>
          <div
            :class="[
              'reviews__add-review-cta-wrap',
              {
                'reviews__add-review-cta-wrap--no-reviews': hasNoReviews,
                'reviews__add-review-cta-wrap--cta-hidden': hasAddReviewCtaHidden,
              },
            ]"
          >
            <Button
              v-show="!hasAddReviewCtaHidden"
              ref="addReviewCta"
              class="reviews__add-review-cta"
              :isInactive="hasSubmittedReview"
              :color="BUTTON_STYLES.color.white"
              type="button"
              @click="handleAddReviewClick"
              >{{ $t('leave_review') }}</Button
            >
          </div>
        </div>
      </transition>

      <LoadingIndicator v-if="!hasFetchedFirstReviews" class="reviews__loader" />

      <template v-else-if="reviewTotal > 0">
        <transition-group name="slide-and-appear" tag="div">
          <div
            v-for="review in reviews"
            :key="review.id"
            ref="reviewBlock"
            class="reviews__block"
            v-data-test-id="'review-block'"
          >
            <SeoReviewRating
              :productName="product.name"
              :productDescription="product.description"
              :author="review.author"
              :ratingValue="review.rating"
              :reviewBody="review.review"
            />
            <div class="reviews__details">
              <div class="reviews__details-rating">
                <FiveStarsRating :rating="review.rating" :isSmall="true" />
              </div>
              <div class="reviews__details-row">
                <span class="reviews__details-label">{{ $t('activity_label') }}:</span>
                <span class="reviews__details-data">{{
                  review.runningReason ? $t(`activity.${review.runningReason}`) : '-'
                }}</span>
              </div>
              <div class="reviews__details-row">
                <span class="reviews__details-label">{{ $t('typical_distance_label') }}:</span>
                <span class="reviews__details-data">{{
                  review.runningDistance ? $t(`typical_distance.${review.runningDistance}`) : '-'
                }}</span>
              </div>
              <div class="reviews__details-row" :title="renderTitleizedString(review.location)">
                <span class="reviews__details-label">{{ $t('location') }}:</span>
                <span class="reviews__details-data" v-data-test-id="'review-location'">{{
                  renderTitleizedString(review.location)
                }}</span>
              </div>
            </div>
            <div class="reviews__copy-wrap">
              <h4 class="reviews__title">
                {{ renderTitleizedString(review.title) }}
              </h4>
              <FormattedText
                class="reviews__copy"
                :text="review.review"
                :visibleLineCount="isMqMedium ? 5 : 4"
                :options="formattedTextOptions"
              />
              <div class="reviews__author">
                {{ renderTitleizedString(review.author) }}
              </div>
            </div>
          </div>
        </transition-group>

        <div v-if="hasMoreReviewsToLoad" class="reviews__load-cta-wrap">
          <Button
            :color="BUTTON_STYLES.color.white"
            :isSubmitting="isLoadingMoreReviews"
            type="button"
            class="reviews__load-cta"
            v-data-test-id="'review-load-more-cta'"
            @click="loadMoreReviews"
            >{{ $t('load_more') }}</Button
          >
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import { requestAnimationFrameAsync, listenToTransitionEndOnce } from '@/helpers/animation'
import { observeIntersection } from '@/helpers/intersection-observer'
import { scrollSmoothlyToElement } from '@/helpers/scroll'
import { titleize, isEmpty } from '@/helpers/string'
import { REVIEWS_PER_PAGE } from '@/stores/product'
import SeoReviewRating from './SeoReviewRating.vue'
import FiveStarsRating from '@/components/_shared/Form/FiveStarsRating/FiveStarsRating.vue'
import FormattedText from '@/components/_shared/FormattedText.vue'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'

const SLIDE_AND_APPEAR_TRANSLATION = 50

export default {
  name: 'Reviews',
  components: {
    SeoReviewRating,
    FiveStarsRating,
    FormattedText,
    Button,
    LoadingIndicator,
  },

  data() {
    return {
      hasNoReviews: false,
      hasSubmittedReview: false,
      BUTTON_STYLES,
    }
  },

  computed: {
    ...mapState('product', [
      'isLoaded',
      'reviews',
      'reviewTotal',
      'reviewPageLoaded',
      'isLoadingMoreReviews',
      'hasFetchedFirstReviews',
      'reviewRatingAverage',
      'reviewTotal',
    ]),
    ...mapGetters({
      isMqTiny: 'viewport/isMqTiny',
      isMqMedium: 'viewport/isMqMedium',
      product: 'product/product',
    }),

    hasAddReviewCtaHidden() {
      return this.hasSubmittedReview && !this.hasNoReviews
    },

    hasMoreReviewsToLoad() {
      return this.reviewPageLoaded * REVIEWS_PER_PAGE < this.reviewTotal
    },

    formattedTextOptions() {
      return this.isMqTiny ? { maxLengthOfConcat: 10 } : {}
    },
  },

  watch: {
    async hasFetchedFirstReviews(fetched) {
      if (fetched && this.reviewTotal === 0) {
        const hasCtaInViewport =
          this.$refs.addReviewCta.$el.getBoundingClientRect().top < window.innerHeight
        const currentHeight = this.$el.offsetHeight

        this.hasNoReviews = true

        if (hasCtaInViewport) {
          await requestAnimationFrameAsync()
          const newHeight = this.$el.offsetHeight
          this.$el.style.height = `${currentHeight}px`

          await requestAnimationFrameAsync()
          this.$el.style.height = `${newHeight}px`

          listenToTransitionEndOnce(this.$el, ['height'], () => (this.$el.style.height = ''))
        }
      }
    },
  },

  mounted() {
    observeIntersection(this.$el, () => {
      this.fetchReviews()
      this.preloadLeaveReviewOverlay()
    })
  },

  methods: {
    ...mapActions({
      fetchReviews: 'product/fetchReviews',
      showLeaveReviewOverlay: 'overlay/showLeaveReviewOverlay',
      preloadLeaveReviewOverlay: 'overlay/preloadLeaveReviewOverlay',
    }),

    renderTitleizedString(string) {
      return !isEmpty(string) ? titleize(string) : '-'
    },

    async loadMoreReviews(event) {
      event.target.blur()
      await this.$store.dispatch('product/loadMoreReviews')
      await this.$nextTick()

      const firstIndexOfNewReviews = (this.reviewPageLoaded - 1) * REVIEWS_PER_PAGE
      scrollSmoothlyToElement(this.$refs.reviewBlock[firstIndexOfNewReviews], {
        duration: On.TransitionTimes.slower,
        offset: -SLIDE_AND_APPEAR_TRANSLATION - 40,
      })
    },

    handleAddReviewClick() {
      this.hasSubmittedReview && this.hasNoReviews
        ? this.$refs.addReviewCta.shake()
        : this.showLeaveReviewOverlay({ product: this.product, onSuccess: this.onReviewSubmit })
      notifyExternalServices({
        action: ACTIONS.productInteraction,
        details: { action: 'review', label: 'add' },
      })
    },

    onReviewSubmit() {
      this.hasSubmittedReview = true
      notifyExternalServices({
        action: ACTIONS.productInteraction,
        details: { action: 'review', label: 'submit' },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Reviews';
</style>

<i18n src="@/locales/reviews.json">
</i18n>
